import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Loading, MessageBanner, Modal, ModalFooter, RadioButton, RadioButtonGroup, TextArea, TextField, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { AgendaChangeModal } from "./AgendaChangeModal";
import { AgendaHandlerChoice } from "./AgendaHandlerChoice";
import { NEW_SPECIAL_ISSUE, PeriodicalIssuePickerGrid, } from "./PeriodicalIssuePickerGrid";
import { LINKABLE_PERIODICALS_LIMIT, createSpecialIssueLink, fetchLinkableIssues, fetchLinkableVolumes, fetchLinkablePeriodicals, linkIssueToMonograph, } from "../../api/agendaService";
import { defaultVolumeId } from "../../utils/linkableIssues";
import { ISSUE_LABEL_MAX_LENGTH } from "../../utils/issueLabel";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useServerErrorMessage } from "../../hooks/useServerErrorMessage";
import { LINKABLE_ISSUES_KEY, LINKABLE_PERIODICALS_KEY, LINKABLE_VOLUMES_KEY, } from "../../utils/agendaQueries";
import css from "./Agenda.module.css";
var SEARCH_MIN_LENGTH = 2;
var INPUT_DEBOUNCE_MS = 400;
var NO_VOLUMES = [];
var NO_ISSUES = [];
var periodicalLabel = function (periodical) {
    var _a;
    return [
        (_a = periodical.name) !== null && _a !== void 0 ? _a : "–",
        periodical.issn ? "(".concat(periodical.issn, ")") : null,
        periodical.publisherName ? "- ".concat(periodical.publisherName) : null,
    ]
        .filter(Boolean)
        .join(" ");
};
var parseIssueNumber = function (value) {
    var parsed = Number(value.trim());
    return value.trim() && Number.isInteger(parsed) && parsed > 0
        ? parsed
        : undefined;
};
export var LinkToPeriodicalDialog = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k;
    var open = _a.open, monographTitleId = _a.monographTitleId, monographName = _a.monographName, onClose = _a.onClose, onChanged = _a.onChanged;
    var formatMessage = useIntl().formatMessage;
    var kyInstance = useOkapiKy();
    var _l = useState("search"), step = _l[0], setStep = _l[1];
    var _m = useState(""), search = _m[0], setSearch = _m[1];
    var _o = useState(null), periodicalId = _o[0], setPeriodicalId = _o[1];
    var _p = useState(""), pickedVolumeId = _p[0], setPickedVolumeId = _p[1];
    var _q = useState(null), issueChoice = _q[0], setIssueChoice = _q[1];
    var _r = useState(""), newLabel = _r[0], setNewLabel = _r[1];
    var _s = useState(""), newNumber = _s[0], setNewNumber = _s[1];
    var _t = useState(""), specialNote = _t[0], setSpecialNote = _t[1];
    var _u = useState(null), handler = _u[0], setHandler = _u[1];
    var _v = useState(""), note = _v[0], setNote = _v[1];
    useEffect(function () {
        if (!open) {
            setStep("search");
            setSearch("");
            setPeriodicalId(null);
            setPickedVolumeId("");
            setIssueChoice(null);
            setNewLabel("");
            setNewNumber("");
            setSpecialNote("");
            setHandler(null);
            setNote("");
        }
    }, [open]);
    var debouncedSearch = useDebouncedValue(search.trim(), INPUT_DEBOUNCE_MS);
    var periodicalsQuery = useQuery([LINKABLE_PERIODICALS_KEY, debouncedSearch], function () { return fetchLinkablePeriodicals(kyInstance, debouncedSearch); }, {
        enabled: open && debouncedSearch.length >= SEARCH_MIN_LENGTH,
        keepPreviousData: true,
    });
    var periodicals = (_c = (_b = periodicalsQuery.data) === null || _b === void 0 ? void 0 : _b.items) !== null && _c !== void 0 ? _c : [];
    var volumesQuery = useQuery([LINKABLE_VOLUMES_KEY, periodicalId], function () { return fetchLinkableVolumes(kyInstance, periodicalId !== null && periodicalId !== void 0 ? periodicalId : ""); }, { enabled: open && !!periodicalId && step !== "search" });
    var volumes = (_e = (_d = volumesQuery.data) === null || _d === void 0 ? void 0 : _d.volumes) !== null && _e !== void 0 ? _e : NO_VOLUMES;
    var volumeId = pickedVolumeId || defaultVolumeId(volumes);
    var issuesQuery = useQuery([LINKABLE_ISSUES_KEY, periodicalId, volumeId], function () { return fetchLinkableIssues(kyInstance, periodicalId !== null && periodicalId !== void 0 ? periodicalId : "", volumeId); }, { enabled: open && !!periodicalId && !!volumeId && step !== "search" });
    var issues = useMemo(function () { var _a, _b; return (_b = (_a = issuesQuery.data) === null || _a === void 0 ? void 0 : _a.volume.issues) !== null && _b !== void 0 ? _b : NO_ISSUES; }, [issuesQuery.data]);
    var periodicalsError = useServerErrorMessage(periodicalsQuery.error, "ui-br-legaldeposits.agenda.linkPeriodical.searchFailed");
    var volumesError = useServerErrorMessage(volumesQuery.error, "ui-br-legaldeposits.agenda.linkPeriodical.issuesFailed");
    var issuesError = useServerErrorMessage(issuesQuery.error, "ui-br-legaldeposits.agenda.linkPeriodical.issuesFailed");
    var isNewIssue = issueChoice === NEW_SPECIAL_ISSUE;
    var newLabelValue = newLabel.trim();
    var newIssueNumber = parseIssueNumber(newNumber);
    var newNumberInvalid = newNumber.trim() !== "" && newIssueNumber === undefined;
    var noteValue = note.trim() || undefined;
    var issueChosen = isNewIssue
        ? newLabelValue.length > 0 && !newNumberInvalid && !!volumeId
        : !!issueChoice;
    var chosenIssueLabel = isNewIssue
        ? newLabelValue
        : ((_g = (_f = issues.find(function (issue) { return issue.id === issueChoice; })) === null || _f === void 0 ? void 0 : _f.label) !== null && _g !== void 0 ? _g : "");
    var chosenPeriodical = periodicals.find(function (periodical) { return periodical.titleId === periodicalId; });
    var run = useCallback(function (dryRun) {
        if (!periodicalId || !handler || !issueChoice) {
            return Promise.reject(new Error("A periodical, an issue and a handler must be chosen"));
        }
        if (issueChoice === NEW_SPECIAL_ISSUE) {
            return createSpecialIssueLink(kyInstance, monographTitleId, {
                periodicalTitleId: periodicalId,
                label: newLabelValue,
                volumeId: volumeId || undefined,
                issueNumber: newIssueNumber,
                specialNote: specialNote.trim() || undefined,
                agenda: handler,
                note: noteValue,
            }, dryRun);
        }
        return linkIssueToMonograph(kyInstance, issueChoice, { monographTitleId: monographTitleId, agenda: handler, note: noteValue }, dryRun);
    }, [
        kyInstance,
        monographTitleId,
        periodicalId,
        handler,
        issueChoice,
        newLabelValue,
        volumeId,
        newIssueNumber,
        specialNote,
        noteValue,
    ]);
    var previewKey = useMemo(function () { return [
        "link-periodical",
        monographTitleId,
        periodicalId,
        volumeId,
        issueChoice,
        handler,
        isNewIssue ? newLabelValue : null,
        isNewIssue ? (newIssueNumber !== null && newIssueNumber !== void 0 ? newIssueNumber : null) : null,
    ]; }, [
        monographTitleId,
        periodicalId,
        volumeId,
        issueChoice,
        handler,
        isNewIssue,
        newLabelValue,
        newIssueNumber,
    ]);
    var handleSearchChange = useCallback(function (e) { return setSearch(e.target.value); }, []);
    var handlePeriodicalChange = useCallback(function (value) { return function () {
        setPeriodicalId(value);
        setPickedVolumeId("");
        setIssueChoice(null);
    }; }, []);
    var handleVolumeChange = useCallback(function (value) {
        setPickedVolumeId(value);
        setIssueChoice(null);
    }, []);
    var handleNewLabelChange = useCallback(function (e) { return setNewLabel(e.target.value); }, []);
    var handleNewNumberChange = useCallback(function (e) { return setNewNumber(e.target.value); }, []);
    var handleSpecialNoteChange = useCallback(function (e) { return setSpecialNote(e.target.value); }, []);
    var handleNoteChange = useCallback(function (e) { return setNote(e.target.value); }, []);
    var goTo = useCallback(function (next) { return function () { return setStep(next); }; }, []);
    var searchTooShort = debouncedSearch.length < SEARCH_MIN_LENGTH;
    var moreResults = ((_j = (_h = periodicalsQuery.data) === null || _h === void 0 ? void 0 : _h.totalRecords) !== null && _j !== void 0 ? _j : 0) > LINKABLE_PERIODICALS_LIMIT;
    var heading = formatMessage({ id: "ui-br-legaldeposits.agenda.linkPeriodical.heading" }, { name: monographName });
    return (React.createElement(React.Fragment, null,
        React.createElement(Modal, { open: open && step === "search", label: heading, onClose: onClose, dismissible: true, size: "medium", footer: React.createElement(ModalFooter, null,
                React.createElement(Button, { buttonStyle: "primary", onClick: goTo("grid"), disabled: !periodicalId, "data-test-id": "link-periodical-next" },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.wizard.next" })),
                React.createElement(Button, { onClick: onClose },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.button.cancel" }))) },
            React.createElement("p", null,
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.linkPeriodical.intro" })),
            React.createElement(TextField, { label: formatMessage({
                    id: "ui-br-legaldeposits.agenda.linkPeriodical.search.label",
                }), placeholder: formatMessage({
                    id: "ui-br-legaldeposits.agenda.linkPeriodical.search.placeholder",
                }), value: search, onChange: handleSearchChange, autoFocus: true, "data-test-id": "link-periodical-search" }),
            searchTooShort && !periodicalId && (React.createElement("p", { className: css.hint },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.linkPeriodical.search.hint" }))),
            !searchTooShort && periodicalsQuery.isFetching && React.createElement(Loading, null),
            periodicalsError && (React.createElement(MessageBanner, { type: "error" }, periodicalsError)),
            !searchTooShort &&
                periodicalsQuery.isSuccess &&
                periodicals.length === 0 && (React.createElement("p", { className: css.hint },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.linkPeriodical.search.empty" }))),
            !searchTooShort && periodicals.length > 0 && (React.createElement(RadioButtonGroup, { label: formatMessage({
                    id: "ui-br-legaldeposits.agenda.linkPeriodical.periodical.label",
                }) }, periodicals.map(function (periodical) { return (React.createElement(RadioButton, { key: periodical.titleId, name: "link-periodical-target", label: periodicalLabel(periodical), checked: periodicalId === periodical.titleId, onChange: handlePeriodicalChange(periodical.titleId) })); }))),
            !searchTooShort && moreResults && (React.createElement("p", { className: css.hint },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.linkPeriodical.search.more", values: { count: LINKABLE_PERIODICALS_LIMIT } })))),
        React.createElement(Modal, { open: open && step === "grid", label: formatMessage({ id: "ui-br-legaldeposits.agenda.linkPeriodical.grid.heading" }, {
                periodical: chosenPeriodical
                    ? periodicalLabel(chosenPeriodical)
                    : "",
            }), onClose: onClose, dismissible: true, size: "large", footer: React.createElement(ModalFooter, null,
                React.createElement(Button, { buttonStyle: "primary", onClick: goTo("handler"), disabled: !issueChosen, "data-test-id": "link-periodical-grid-next" },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.wizard.next" })),
                React.createElement(Button, { onClick: goTo("search") },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.wizard.back" }))) },
            React.createElement("p", { className: css.hint },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.linkPeriodical.grid.hint" })),
            volumesQuery.isLoading && React.createElement(Loading, null),
            volumesError && (React.createElement(MessageBanner, { type: "error" }, volumesError)),
            issuesError && (React.createElement(MessageBanner, { type: "error" }, issuesError)),
            volumesQuery.isSuccess && (React.createElement(PeriodicalIssuePickerGrid, { volumes: volumes, volumeId: volumeId, onVolumeChange: handleVolumeChange, issues: issues, issuesLoading: issuesQuery.isFetching, selected: issueChoice, onSelect: setIssueChoice })),
            isNewIssue && (React.createElement("div", { className: css.section },
                React.createElement(TextField, { label: formatMessage({
                        id: "ui-br-legaldeposits.agenda.linkPeriodical.new.label",
                    }), value: newLabel, onChange: handleNewLabelChange, maxLength: ISSUE_LABEL_MAX_LENGTH, required: true }),
                React.createElement(TextField, { label: formatMessage({
                        id: "ui-br-legaldeposits.agenda.linkPeriodical.new.number",
                    }), value: newNumber, onChange: handleNewNumberChange, error: newNumberInvalid
                        ? formatMessage({
                            id: "ui-br-legaldeposits.agenda.linkPeriodical.new.numberInvalid",
                        })
                        : undefined }),
                React.createElement(TextField, { label: formatMessage({
                        id: "ui-br-legaldeposits.agenda.linkPeriodical.new.specialNote",
                    }), value: specialNote, onChange: handleSpecialNoteChange })))),
        React.createElement(AgendaChangeModal, { open: open && step === "handler", heading: heading, previewKey: previewKey, canSubmit: issueChosen && handler !== null, run: run, impactScope: "linkedPair", onClose: onClose, onChanged: onChanged },
            React.createElement("p", null,
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.linkPeriodical.handler.intro", values: {
                        issue: chosenIssueLabel,
                        periodical: (_k = chosenPeriodical === null || chosenPeriodical === void 0 ? void 0 : chosenPeriodical.name) !== null && _k !== void 0 ? _k : "",
                    } })),
            React.createElement(AgendaHandlerChoice, { name: "link-periodical-handler", side: "monographs", value: handler, onChange: setHandler }),
            React.createElement(TextArea, { label: formatMessage({
                    id: "ui-br-legaldeposits.agenda.dialog.note.label",
                }), value: note, onChange: handleNoteChange }),
            React.createElement("div", { className: css.fieldActions },
                React.createElement(Button, { onClick: goTo("grid"), marginBottom0: true },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.wizard.back" }))))));
};
